// [PURPOSE]: Styled dark-themed confirm dialog replacement for window.confirm()
// [WHY]: Browser confirm() breaks dark theme and feels out of place
// [INTEGRATION]: Used via useConfirm() hook from ConfirmContext

import React, { useEffect, useRef } from 'react';
import { Z } from '../constants/zLayers';
import { useModalEscape } from '../hooks/useModalEscape';

const ConfirmDialog = ({
  isOpen,
  title = 'Confirm',
  message = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'blue', // 'blue' for non-destructive, 'red' for destructive, 'amber' for caution
  onConfirm,
  onCancel
}) => {
  const confirmButtonRef = useRef(null);

  // [APR 2026]: Standardize Escape dismissal — Escape is an alias for Cancel,
  // never Confirm (would be a destructive default for a 'red' confirmColor).
  useModalEscape(isOpen, onCancel);

  // [UX]: Auto-focus confirm button when dialog opens
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const colorClasses = confirmColor === 'red'
    ? 'bg-red-600 hover:bg-red-500 focus:ring-red-500'
    : confirmColor === 'amber'
    ? 'bg-amber-600 hover:bg-amber-500 focus:ring-amber-500'
    : 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: Z.CONFIRM_DIALOG }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-message"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-60"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="relative bg-gray-800 rounded-xl shadow-2xl border border-gray-700 w-full max-w-sm overflow-hidden">
        <div className="p-5">
          <h3 id="confirm-title" className="text-lg font-bold text-white mb-2">
            {title}
          </h3>
          <p id="confirm-message" className="text-sm text-gray-300 leading-relaxed">
            {message}
          </p>
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 ${colorClasses} text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
